import { Injectable, computed, inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { PermissionsService } from '../../services/permissions.service';
import { MENU_DATA, MenuItem, MenuSection } from '../../layouts/sidebar/menu-data';

/**
 * Menu visible para el usuario: sidebar, menu movil y buscador del encabezado.
 *
 * La opcion que exige cada pantalla se lee de la propia ruta (`data.optionId`, la misma que
 * revisa `permissionsGuard`), no de una lista aparte. Con la lista aparte, una pantalla que
 * tenia guardia pero no estaba en la lista se pintaba para todos y al abrirla la guardia
 * mandaba a "Modulo en Desarrollo" (folio 7B17C3).
 *
 * Una opcion sin permiso de Leer no se pinta; un grupo sin ninguna opcion visible tampoco.
 * Las pantallas sin `optionId` (el Dashboard) quedan visibles para todos.
 */
@Injectable({ providedIn: 'root' })
export class MenuAccessService {
  private readonly router  = inject(Router);
  private readonly permSvc = inject(PermissionsService);

  private readonly optionByRoute = buildOptionIndex(this.router.config);

  readonly sections = computed<MenuSection[]>(() => {
    if (!this.permSvc.isLoaded() || this.permSvc.isSuperAdmin()) return MENU_DATA;

    return MENU_DATA
      .map(section => ({ ...section, children: this.filterItems(section.children) }))
      .filter(section => section.children.length > 0);
  });

  /** Si el usuario puede abrir la ruta del menu (`/catalogos/areas`). */
  canOpen(route: string | undefined): boolean {
    if (!route) return true;
    const optionId = this.optionByRoute.get(route);
    if (optionId === undefined) return true;
    return this.permSvc.canRead(optionId);
  }

  private filterItems(items: MenuItem[]): MenuItem[] {
    const result: MenuItem[] = [];
    for (const item of items) {
      if (item.children) {
        const children = this.filterItems(item.children);
        if (children.length > 0) result.push({ ...item, children });
      } else if (this.canOpen(item.route)) {
        result.push(item);
      }
    }
    return result;
  }
}

/** Ruta absoluta -> optionId de su guardia. Los alias (`redirectTo`) heredan el de su destino. */
function buildOptionIndex(config: Routes): Map<string, number> {
  const options   = new Map<string, number>();
  const redirects = new Map<string, string>();

  const join = (...parts: (string | undefined)[]) => '/' + parts.filter(Boolean).join('/');

  const walk = (routes: Routes, prefix: string) => {
    for (const route of routes) {
      if (route.path === undefined || route.path.includes('*')) continue;
      const full = join(prefix, route.path);

      const optionId = route.data?.['optionId'];
      if (typeof optionId === 'number') options.set(full, optionId);

      if (typeof route.redirectTo === 'string') {
        const target = route.redirectTo.startsWith('/')
          ? route.redirectTo
          : join(prefix, route.redirectTo);
        redirects.set(full, target);
      }

      if (route.children) walk(route.children, full.slice(1));
    }
  };

  walk(config, '');

  for (const [from, to] of redirects) {
    const optionId = options.get(to);
    if (optionId !== undefined && !options.has(from)) options.set(from, optionId);
  }
  return options;
}
